"use client";

import { useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { createPersonalityTriads } from "@/lib/atsa/personality";

type Phase = "options" | "running" | "result";
type Selection = { most: number | null; least: number | null; startedAt: number; completedAt: number | null };

export default function PersonalityAssessmentPage() {
  const triads = useMemo(() => createPersonalityTriads(ATSA_PROFILE_2026.personality.triads), []);
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [page, setPage] = useState(0);
  const [practiceCount, setPracticeCount] = useState(12);
  const [responses, setResponses] = useState<Record<string, Selection>>({});
  const testStartedAtRef = useRef(0);

  const effectiveCount = mode === "simulation" ? ATSA_PROFILE_2026.personality.triads : practiceCount;
  const activeTriads = triads.slice(0, effectiveCount);
  const perPage = ATSA_PROFILE_2026.personality.triadsPerPage;
  const totalPages = Math.ceil(activeTriads.length / perPage);
  const visible = activeTriads.slice(page * perPage, page * perPage + perPage);

  function begin() {
    setResponses({});
    setPage(0);
    testStartedAtRef.current = performance.now();
    setPhase("running");
  }

  function choose(triadId: string, type: "most" | "least", index: number) {
    setResponses((current) => {
      const existing = current[triadId] ?? { most: null, least: null, startedAt: performance.now(), completedAt: null };
      const next: Selection = { ...existing, [type]: index };
      if (type === "most" && next.least === index) next.least = null;
      if (type === "least" && next.most === index) next.most = null;
      if (next.most !== null && next.least !== null) next.completedAt = performance.now();
      return { ...current, [triadId]: next };
    });
  }

  const pageComplete = visible.every((triad) => {
    const response = responses[triad.id];
    return response?.most !== null && response?.least !== null;
  });

  function next() {
    if (!pageComplete) return;
    if (page + 1 < totalPages) setPage((value) => value + 1);
    else setPhase("result");
  }

  const completed = Object.values(responses).filter((response) => response.most !== null && response.least !== null).length;
  const responseTimes = Object.values(responses).filter((response) => response.completedAt !== null).map((response) => (response.completedAt! - response.startedAt) / 1000);
  const rapid = responseTimes.filter((seconds) => seconds < 1.2).length;
  const activeSimulation = mode === "simulation" && phase === "running";

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Personality Assessment"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" ? `Progress ${Math.min((page + 1) * perPage, effectiveCount)} of ${effectiveCount}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? begin : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 5</p>
              <h2>Forced-Choice Personality Format</h2>
              <p>Select one statement as Most Like Me and a different statement as Least Like Me. This module teaches the format only. It does not claim to reproduce the FAA scoring key.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") setPracticeCount(108);
            }} />
            {mode === "training" && (
              <div className="control-group">
                <label htmlFor="personality-count">Practice triads</label>
                <select id="personality-count" value={practiceCount} onChange={(event) => setPracticeCount(Number(event.target.value))}>
                  <option value={6}>6</option><option value={12}>12</option><option value={24}>24</option><option value={108}>108</option>
                </select>
              </div>
            )}
            <ol className="instructions">
              <li>Each triad contains three similarly desirable statements.</li>
              <li>Choose exactly one Most and one Least response.</li>
              <li>The fidelity profile contains 108 triads and is untimed.</li>
              <li>No personality response is marked correct or incorrect.</li>
            </ol>
            <button className="primary-button" onClick={begin}>Start {mode === "simulation" ? "Simulation" : "Practice"}</button>
          </section>
        )}

        {phase === "running" && (
          <section className={activeSimulation ? "fidelity-personality" : "panel"}>
            <p className="personality-instruction">For each group, select the statement that is MOST like you and the statement that is LEAST like you.</p>
            <div className="personality-grid">
              {visible.map((triad, visibleIndex) => {
                const response = responses[triad.id];
                return (
                  <div className="statement-group light-statement-group" key={triad.id}>
                    <div className="statement-title">Group {page * perPage + visibleIndex + 1}</div>
                    <div className="statement-head"><span>Most</span><span>Least</span><span>Statement</span></div>
                    {triad.statements.map((statement, index) => (
                      <div className="statement-row" key={statement.id}>
                        <label><input aria-label={`Most: ${statement.text}`} type="radio" name={`${triad.id}-most`} checked={response?.most === index} onChange={() => choose(triad.id, "most", index)} disabled={response?.least === index} /></label>
                        <label><input aria-label={`Least: ${statement.text}`} type="radio" name={`${triad.id}-least`} checked={response?.least === index} onChange={() => choose(triad.id, "least", index)} disabled={response?.most === index} /></label>
                        <p>{statement.text}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="personality-actions">
              {mode === "training" && page > 0 && <button className="secondary-button" onClick={() => setPage((value) => value - 1)}>Previous</button>}
              <button className="primary-button" onClick={next} disabled={!pageComplete}>{page + 1 === totalPages ? "Finish" : "Continue"}</button>
            </div>
          </section>
        )}

        {phase === "result" && (
          <Results
            section="Personality Format"
            title="Personality Format Complete"
            metrics={[
              { label: "Triads completed", value: completed },
              { label: "Total triads", value: effectiveCount },
              { label: "Rapid responses", value: rapid },
              { label: "Elapsed", value: `${Math.round((performance.now() - testStartedAtRef.current) / 60000)} min` },
            ]}
            onAgain={begin}
            onOptions={() => setPhase("options")}
          />
        )}
      </TestChrome>
    </AppShell>
  );
}
